import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  TaskRecord,
  TaskStatusUpdatePayload,
  TaskDetailRecord,
  ScheduleDetailRecord,
  TaskTypeRecord,
  SliderTimerState,
} from "@ServerTypes/tasks/tasks.schema";

// Slice to store the list of tasks returned by APIs
const taskDataSlice = createSlice({
  name: "taskData",
  initialState: [] as TaskRecord[],
  reducers: {
    setTaskData: (_state: TaskRecord[], action: PayloadAction<TaskRecord[]>) => action.payload,
    updateTaskData: (state: TaskRecord[], action: PayloadAction<TaskStatusUpdatePayload>) => {
      const tempState = [...state];
      const taskId = action.payload.id;
      if (tempState[taskId] && tempState[taskId].TASKS_STATUS) {
        tempState[taskId].TASKS_STATUS!.statusId = action.payload.status;
      }
      return tempState;
    },
    removeTaskData: (state: TaskRecord[]) => {
      const localState = [...state];
      return localState;
    },
  },
});

// Slice to store task detail information
const taskDetailsSlice = createSlice({
  name: "taskDetails",
  initialState: {} as TaskDetailRecord,
  reducers: {
    setTaskDetails: (state: TaskDetailRecord, action: PayloadAction<Partial<TaskDetailRecord>>) => ({
      ...state,
      ...action.payload,
    }),
  },
});

// Slice to store gantt schedule information
const ganttDetailsSlice = createSlice({
  name: "ganttDetails",
  initialState: {} as ScheduleDetailRecord,
  reducers: {
    setScheduleDetails: (_state: ScheduleDetailRecord, action: PayloadAction<ScheduleDetailRecord>) => ({
      ...action.payload,
    }),
    deleteScheduleDetails: () => ({}) as ScheduleDetailRecord,
  },
});

// Slice to store task types
const taskTypesSlice = createSlice({
  name: "taskTypes",
  initialState: {} as Record<string, TaskTypeRecord[]>,
  reducers: {
    setTaskTypes: (state: Record<string, TaskTypeRecord[]>, action: PayloadAction<TaskTypeRecord[]>) => ({
      ...state,
      ...(action.payload as unknown as Record<string, TaskTypeRecord[]>),
    }),
  },
});

// Slice used for slider timer data
const sliderTimerSlice = createSlice({
  name: "sliderTimer",
  initialState: {} as SliderTimerState,
  reducers: {
    setScheduleSliderTime: (state: SliderTimerState, action: PayloadAction<Partial<SliderTimerState>>) => ({
      ...state,
      ...action.payload,
    }),
  },
});

// Selection set elements: taskId -> array of elementIds
type SelectionSetElementsState = Record<string, string[]>;

// Slice for selection set elements associated with tasks
const selectionSetElementsSlice = createSlice({
  name: "selectionSetTaskElements",
  initialState: {} as SelectionSetElementsState,
  reducers: {
    setSelectionSetTaskElements: (
      state: SelectionSetElementsState,
      action: PayloadAction<SelectionSetElementsState>,
    ) => ({
      ...state,
      ...action.payload,
    }),
  },
});

// Slice for running tasks cost (numeric value)
const runningTasksCostSlice = createSlice({
  name: "runningTasksCost",
  initialState: 0,
  reducers: {
    setTaskCost: (_state: number, action: PayloadAction<number>) => action.payload,
  },
});

export const { setTaskData, updateTaskData, removeTaskData } = taskDataSlice.actions;
export const { setTaskDetails } = taskDetailsSlice.actions;
export const { setScheduleDetails, deleteScheduleDetails } = ganttDetailsSlice.actions;
export const { setTaskTypes } = taskTypesSlice.actions;
export const { setScheduleSliderTime } = sliderTimerSlice.actions;
export const { setSelectionSetTaskElements } = selectionSetElementsSlice.actions;
export const { setTaskCost } = runningTasksCostSlice.actions;

export const TaskData = taskDataSlice.reducer;
export const TaskDetails = taskDetailsSlice.reducer;
export const GanttDetails = ganttDetailsSlice.reducer;
export const TaskTypes = taskTypesSlice.reducer;
export const sliderTimer = sliderTimerSlice.reducer;
export const selectionSetTaskElements = selectionSetElementsSlice.reducer;
export const runningTasksCost = runningTasksCostSlice.reducer;

export default {
  TaskData,
  TaskDetails,
  GanttDetails,
  TaskTypes,
  sliderTimer,
  selectionSetTaskElements,
  runningTasksCost,
};
